import { ref, Ref, watch } from 'vue';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { fetchAdvancedSnapshotExportUrl } from '@/pages/OperationsInsights/services/fetchAdvancedSnapshotExportUrl';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { ExpiringUrl, urlLifetimeSec } from '@/pages/OperationsInsights/utils/ExpiringUrl';

export function useExplorerExport(queryParams: Ref<ApiQueryParams>, selectedStatus: Ref<string>, selectedDate: Ref<Date | undefined>) {
    const logger = useLogger('useExplorerExport');
    const exportUrl = ref<ExpiringUrl | undefined>();
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const fetchExport = async () => {
        if (!queryParams.value || !selectedDate.value) {
            return;
        }

        exportUrl.value = undefined;
        isLoading.value = true;
        error.value = null;

        try {
            const data = await fetchAdvancedSnapshotExportUrl({
                ...queryParams.value,
                type: selectedStatus.value,
                snapshotDate: selectedDate.value,
                expiresIn: urlLifetimeSec,
            });

            if (data.exportUrl) {
                exportUrl.value = new ExpiringUrl(data.exportUrl);
            } else {
                error.value = 'Failed to fetch export URL';
                logger.error('No exportUrl in response', { data });
            }
        } catch (err) {
            error.value = 'Failed to fetch export URL';
            logger.error('Failed to fetch export URL', { error: err });
        } finally {
            isLoading.value = false;
        }

        return exportUrl.value ?? undefined;
    };

    watch([selectedDate, selectedStatus, queryParams], () => {
        exportUrl.value = undefined;
    }, { deep: true });

    return {
        fetchExport,
        exportUrl,
        isLoading,
        error,
    };
}
